<template>
  <div class="w-full">
    <div class="hintMessage">
      If yes, please list any hormone(s) used in the past two months. Attach
      separate sheet, if needed, or attach photo copy of prescription(s) for
      hormone medications.
    </div>
    <div class="table-container">
      <table class="responsive-table">
        <thead>
          <tr>
            <th class="w-[150px]">Hormone Type</th>
            <th class="w-[80px]">Brand</th>
            <th class="w-[80px]">Delivery</th>
            <th class="w-[75px]">Dosage</th>
            <th class="w-[100px]">Date</th>
            <th class="w-[80px]">Time</th>
            <th class="w-[75px]">Times per Day</th>
            <th class="w-[100px]">How Long Used</th>
            <th class="w-[60px] action-column">
              <div class="action-container"></div>
            </th>
          </tr>
        </thead>
        <tbody v-if="this.tableData.length > 0">
          <tr v-for="(item, index) in this.tableData" :key="index">
            <td class="w-[150px]">{{ item.hormone_type }}</td>
            <td class="w-[100px]">{{ item.brand }}</td>
            <td class="w-[80px]">{{ item.delivery }}</td>
            <td class="w-[75px]">{{ item.dosage }}</td>
            <td class="w-[100px]">{{ item.last_used }}</td>
            <td class="w-[80px]">{{ item.last_used_time}}</td>
            <td class="w-[75px]">{{ item.times_per_day }}</td>
            <td class="w-[100px]">{{ item.how_long_used }}</td>
            <td class="w-[60px] action-column">
              <div class="action-container">
                <EditHormoneDialog
                  :editIndex="index"
                  :hormoneRecord="item"
                  @editMedicationHistory="editMedicationHistory"
                  :disabled="qnrIsCompleted"
                />
                <img
                  src="@/assets/questionnaire/delete_icon.svg"
                  alt="delete"
                  class="deleteIcon cursor-pointer"
                  :class="{ 'disabled-icon': qnrIsCompleted }"
                  @click="openDeleteDialog(index)"
                />
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td :colspan="8" class="emptyMessage">
              No hormones / Medications Added
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <AddHormoneDialog @addToMedicationHistory="addToMedicationHistory" :disabled="qnrIsCompleted"/>
    <el-dialog
      v-model="deleteDialog"
      modal-class="deleteHormoneDialog"
      width="808px"
    >
      <template #title>
        <span class="dialog-title"
          >Are you sure you want to delete this hormone?</span
        >
      </template>
      <div class="dialog-content">
        This action is permanent and cannot be undone.
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button class="cancelBtn" @click="closeDeleteDialog"
            >Cancel</el-button
          >
          <el-button type="deleteHormoneBtn" @click="deleteHormoneRecord"
            >Delete</el-button
          >
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import AddHormoneDialog from "./AddHormoneDialog.vue";
import { useHormoneZoomerStore } from "@/stores/HormoneZoomerStore.ts";
import { mapActions, mapState } from "pinia";
import EditHormoneDialog from "./EditHormoneDialog.vue";

export default {
  name: "HormoneRecord",
  components: {
    AddHormoneDialog,
    EditHormoneDialog,
  },
  data() {
    return {
      tableData: [],
      deleteDialog: false,
      deleteIndex: null,
    };
  },
  watch: {
    hormoneMedication: {
      immediate: true,
      deep: true,
      handler(newValue) {
        // Create a shallow copy to prevent mutation issues with Pinia state
        if(newValue?.length > 0){
          this.tableData = [...newValue];
        }
      },
    },
  },
  computed: {
    ...mapState(() => useHormoneZoomerStore(), ["hormoneMedication", "qnrIsCompleted"]),
  },
  methods: {
    ...mapActions(() => useHormoneZoomerStore(), ["setHormoneMedication"]),
    addToMedicationHistory(value) {
      const hormoneRecord = {
        hormone_type: value.hormoneType,
        brand: value.brand,
        delivery: value.delivery,
        dosage: value.dosage,
        last_used: value.lastUsedDate,
        last_used_time: value.lastUsedTime,
        times_per_day: value.timesPerDay,
        how_long_used: value.howLongUsed,
      };
      this.tableData.push(hormoneRecord);
      this.setHormoneMedication([...this.tableData]);
    },
    editMedicationHistory(value, index) {
      const hormoneRecord = {
        hormone_type: value.hormoneType,
        brand: value.brand,
        delivery: value.delivery,
        dosage: value.dosage,
        last_used: value.lastUsedDate,
        last_used_time: value.lastUsedTime,
        times_per_day: value.timesPerDay,
        how_long_used: value.howLongUsed,
      };
      this.tableData[index] = hormoneRecord;
      this.setHormoneMedication([...this.tableData]);
    },
    openDeleteDialog(index) {
      if (this.qnrIsCompleted) {
        return;
      }
      this.deleteIndex = index;
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.deleteIndex = null;
    },
    deleteHormoneRecord() {
      this.tableData.splice(this.deleteIndex, 1);
      this.setHormoneMedication([...this.tableData]);
      this.deleteDialog = false;
      this.deleteIndex = null;
    },
  },
};
</script>

<style scoped>
.hintMessage {
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.175px;
  color: #000000;
  margin-bottom: 16px;
}

.table-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border: 2px solid #f3f3f3;
  border-radius: 4px;
}

.responsive-table {
  width: 100%;
  table-layout: fixed;
  padding-left: 10px;
}

.responsive-table th,
.responsive-table td {
  text-align: left;
  box-sizing: border-box;
  padding: 2px 8px;
  white-space: normal;
  word-break: break-word;
}

.responsive-table .action-column {
  position: sticky;
  right: 0;
  z-index: 1;
  padding: 0px !important;
  background-color: #fff;
}

.action-container {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.responsive-table th:first-child,
.responsive-table td:first-child {
  padding-left: 10px;
}

.responsive-table th {
  background-color: #f3f6f8 !important;
  font-weight: 500;
  color: #0e4672;
}

.responsive-table tr {
  height: 42px;
  border: 1px solid #f3f3f3;
}

.emptyMessage {
  height: 60px;
  color: #7e8299;
  font-size: 14px;
  padding-left: 250px !important;
}

.deleteIcon {
  display: inline;
  margin-left: 10px;
  vertical-align: middle;
  filter: invert(69%) sepia(20%) saturate(17%) hue-rotate(354deg)
    brightness(87%) contrast(75%);
}

.deleteIcon:hover {
  filter: invert(52%) sepia(85%) saturate(438%) hue-rotate(142deg)
    brightness(90%) contrast(87%);
}

@media only screen and (max-width: 808px) {
  .hintMessage {
    font-size: 14px;
  }

  .responsive-table td,
  .responsive-table th {
    border: 1.5px solid #f3f3f3;
  }

  .action-container {
    box-shadow: -2px 0 5px #0000001a;
  }

  .table-container {
    outline: 2px solid #f3f3f3;
    outline-offset: -4px;
  }

  .emptyMessage {
    padding-left: 50px !important;
  }
}
</style>
